import { DebitRowSelection } from "./types";
import { TableFilter } from "@neonumy/ant-design";
import { clientEntities } from "@neonumy/api-clients";
import { useGetWindowHeight } from "@neonumy/neonumy-ui";
import { Modal, Space } from "antd";
import Text from "antd/es/typography/Text";
import { DATE_FORMAT } from "config/settings";
import dayjs from "dayjs";
import { LIST_CLIENTS_PAGINATED_SELECT } from "queries/entities/clients";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { useParams } from "react-router-dom";
import useNotification from "utils/hooks/useNotification";
import useDirectDebitApi from "views/Payments/hooks/useDirectDebitApi";

interface CancelDebitsPropsTypes {
  id?: ID;
  refetch?: () => void;
  isModalVisible: boolean;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
}
const paginationInit = {
  offset: 0,
  limit: 10,
  orderBy: ["-created_at"],
};
export default function CancelDebits(props: CancelDebitsPropsTypes): React.ReactElement {
  const { id, setIsModalVisible, isModalVisible, refetch } = props;
  const windowHeight = useGetWindowHeight();
  const { companyId }: RouteParams = useParams();
  const { t } = useTranslation();
  const { notificationError } = useNotification();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [selectedRows, setSelectedRows] = useState<DebitRowSelection>({
    selectedRowKeys: [],
    loading: false,
  });
  const { selectedRowKeys } = selectedRows;
  const {
    listSaleDocumentsOfDirectDebits,
    listInvoicesOfDirectDebitsData: dataSource,
    directDebitsTotalCount: totalCount,
    refetchDirectDebits,
    loadingDirectDebits,
    cancelDirectDebit,
  } = useDirectDebitApi();

  useEffect(() => {
    if (isModalVisible) {
      listSaleDocumentsOfDirectDebits({
        variables: {
          id,
          ...paginationInit,
        },
      });
    }
  }, [isModalVisible]);

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleOk = async () => {
    const response = await cancelDirectDebit({
      variables: {
        id,
        documentsIds: selectedRows?.selectedRowKeys as ID[],
      },
    });

    const errors = response?.data?.cancelDirectDebit?.errors;
    const success = response?.data?.cancelDirectDebit?.success;
    if (success) {
      setIsModalVisible(false);
    }
    notificationError(errors);
    refetch!();
  };

  const COLUMNS = [
    {
      title: t("Date"),
      dataIndex: "date",
      key: "date",
      filterType: {
        type: "DateFilter",
        maxDate: dayjs().endOf("day"),
        varName: ["date_Gte", "date_Lte"],
      },
      render: (value: string) => {
        if (value) {
          return (
            <Space size="small" direction="vertical">
              <Text>{dayjs(value).format(DATE_FORMAT)}</Text>
            </Space>
          );
        }
      },
    },
    {
      key: "reference",
      title: t("Reference | Ref"),
      dataIndex: "reference",
    },
    {
      title: t("Client"),
      dataIndex: "client",
      key: "client",
      sorter: true,
      filterType: {
        type: "AsyncSelectFilter",
        varName: "group_ClientId",
        asyncSelectSettings: {
          translations: {
            label: t("Select client"),
            placeholder: t("Select an option"),
            loadingMessage: t("Loading"),
            noOptionsMessage: t("No options"),
            button: {
              search: t("Search"),
              reset: t("Reset"),
            },
          },
          hasQueryFilter: true,
          queryOptions: {
            client: clientEntities,
            dataName: "listEntities",
            query: LIST_CLIENTS_PAGINATED_SELECT,
            variables: {
              filter: {
                companyId,
                entityType: "CLIENT",
                search: "",
              },
              limit: 20,
              orderBy: ["name"],
            },
          },
        },
      },
      render: (_text: unknown, record: ListInvoicesOfDirectDebitsData) => {
        return <span>{`${record?.client?.name || ""}`}</span>;
      },
    },
    {
      key: "debt",
      title: t("Amount to be deducted"),
      dataIndex: "debt",
      render: (_text: unknown, record: ListInvoicesOfDirectDebitsData) => {
        return <span>{`${record?.debt || 0.0}€`}</span>;
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    columnWidth: "4%" as const,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRows({
        ...selectedRows,
        selectedRowKeys: selectedRowKeys,
      });
    },
  };

  const handlePagination = (page: number, size: number) => {
    setPageSize(size);
    setCurrentPage(page);
    refetchDirectDebits({
      id,
      limit: size,
      offset: size * (page - 1),
    });
  };

  return (
    <Modal
      title={t("Cancel payment")}
      open={isModalVisible}
      onOk={handleOk}
      okText={t("Save")}
      okButtonProps={{
        icon: (
          <span className="anticon">
            <FaSave />
          </span>
        ),
      }}
      onCancel={handleCancel}
      width={"70%"}
      className="antd-modal"
    >
      <TableFilter
        size="small"
        loading={loadingDirectDebits}
        rowSelection={rowSelection}
        columns={COLUMNS}
        dataSource={dataSource}
        scroll={{ y: windowHeight - 550 }}
        pagination={{
          onChange: handlePagination,
          showSizeChanger: true,
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showTotal: total => t("Total table items", { count: total }),
        }}
      />
    </Modal>
  );
}
